// Agrupa as operações de uma tag por recurso, para a sidebar da referência.
//
// Uma tag como "Availability" traz 33 endpoints que só diferem no produto
// (/avail/hotel, /avail/flight, /avail/circuit…). Numa lista plana isso vira uma
// coluna de paths quase idênticos, onde achar "o de circuito" exige ler todos.
//
// A chave do grupo é o segmento logo após o prefixo comum a todos os paths da
// tag — derivado, não configurado, porque o prefixo muda de tag para tag
// (/api/v1/avail, /api/v1/backoffice…) e nenhuma lista fixa sobreviveria a um
// backend que versiona a URL.

import { type Operation } from "./types.ts";

/** Um recurso da sidebar: `key` e `label` são `null` no grupo solto. */
export interface OperationGroup {
  key: string | null;
  label: string | null;
  operations: Operation[];
}

// Maior prefixo de segmentos compartilhado por todos os paths, garantindo que
// sobre pelo menos um segmento para servir de chave.
function commonPrefixLength(segmentLists: string[][]): number {
  if (segmentLists.length < 2) return 0;

  const shortest = Math.min(...segmentLists.map((s) => s.length));
  let length = 0;
  while (length < shortest - 1) {
    const candidate = segmentLists[0]?.[length];
    if (!segmentLists.every((segments) => segments[length] === candidate)) break;
    length += 1;
  }
  return length;
}

// serviceOther → "Service Other" · daily-details → "Daily Details"
function humanize(key: string): string {
  return key
    .replace(/[-_]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (c) => c.toUpperCase());
}

const isParam = (segment: string | undefined): boolean => /^\{.*\}$/.test(String(segment || ""));

/**
 * @returns [{ key, label, operations }] — `key` null no grupo solto.
 */
export function groupOperations(
  operations: Operation[],
  { minGroupSize = 2 }: { minGroupSize?: number } = {}
): OperationGroup[] {
  const ungrouped: OperationGroup[] = [{ key: null, label: null, operations }];
  if (operations.length < 4) return ungrouped;

  const segmentLists = operations.map((op) => op.path.split("/").filter(Boolean));
  const prefix = commonPrefixLength(segmentLists);

  const buckets = new Map<string, Operation[]>();
  const loose: Operation[] = [];

  operations.forEach((op, i) => {
    const key = segmentLists[i]?.[prefix];
    // Um {id} logo após o prefixo não descreve recurso nenhum — agrupar por ele
    // criaria um grupo chamado "{id}".
    if (!key || isParam(key)) {
      loose.push(op);
      return;
    }
    const bucket = buckets.get(key);
    if (bucket) bucket.push(op);
    else buckets.set(key, [op]);
  });

  // Grupo de um item só é ruído: vira um cabeçalho para uma linha. Volta a solto.
  for (const [key, ops] of [...buckets.entries()]) {
    if (ops.length < minGroupSize) {
      buckets.delete(key);
      loose.push(...ops);
    }
  }

  // Se sobrou um grupo só (ou nenhum), agrupar não organizou nada.
  if (buckets.size < 2) return ungrouped;

  const groups: OperationGroup[] = [...buckets.entries()]
    .map(([key, ops]) => ({ key, label: humanize(key), operations: ops }))
    .sort((a, b) => (a.label ?? "").localeCompare(b.label ?? ""));

  // Os soltos vão para o fim: são os endpoints que não pertencem a recurso
  // nenhum, e listá-los primeiro empurraria os grupos para baixo da dobra.
  if (loose.length) groups.push({ key: null, label: null, operations: loose });

  return groups;
}

export default groupOperations;
